/**
 * The shell of the question panel — **one** definition, two call sites.
 *
 * It is rendered by `AppShell` (the real `aside`) and by
 * `AssistantPanelPreview` (layer B of the morph, at target width and inert).
 * The whole design depends on the preview being pixel-identical to the real
 * panel at the end of the flight: the 120 ms crossfade then blends two
 * identical images into each other instead of showing a jump. If margins and
 * input box lived in two places, exactly that jump would appear — not right
 * away, but at the next change to either of them. Here it cannot appear by
 * construction.
 *
 * On height (product owner's requirement, 22.09.2026): the panel is **always**
 * limited to the window height and sticks to the top (`sticky top-0`) — even
 * when the page below is longer than the screen. Scrolling happens
 * exclusively *inside* the transcript; for that, every link of the flex chain
 * needs `min-h-0`, otherwise the automatic minimum height (= content)
 * overrides the limit and the input row slides below the window edge.
 * The chain is: `aside` (h = window, flex-col) → content layer
 * `assistant-panel-content` (`min-h-0 flex-1 flex-col`, between the two since
 * A13: it fades in with a delay on open and holds the target width so the
 * width animation doesn't reflow the text) → `OverlayBotChat`
 * (`min-h-0 flex-1 flex-col`) → transcript (`min-h-0 flex-1`) →
 * `MessageScroller` (`overflow-y-auto`) → input form (`shrink-0`).
 *
 * `AssistantPanelPreview` (layer B of the morph) does **not** mirror this
 * intermediate layer — it doesn't need it (no width moves there) and must not
 * need it: the layer takes no space and no height, otherwise the preview would
 * no longer be pixel-identical at the end of the flight.
 *
 * **No header** (product owner's requirement, 22.09.2026): the panel starts
 * directly with the transcript at the top. The assistant's name appeared there
 * twice — the page carries its own header on the same line, and whoever opens
 * the panel knows what they opened. The row only cost height. On mobile the
 * title stays in the tree as `SheetTitle`, but invisible (`sr-only`): Radix's
 * Dialog requires it for screen readers.
 */

import "@/guide/morph/morph.css";

/**
 * The `aside` itself. `assistant-panel-viewport` brings the height with the
 * fallback chain `vh → svh → dvh` (see `morph.css`); `sticky top-0` keeps it at
 * the top edge when the page next to it scrolls. The 400 px live exactly here
 * (`w-[400px]`) and nowhere else — a second constant next to it would be the
 * same number in two places, and Tailwind doesn't accept a computed class anyway.
 */
export const ASSISTANT_PANEL_CLASS =
  "assistant-panel-viewport sticky top-0 flex w-[400px] max-w-[40vw] shrink-0 flex-col overflow-hidden border-l bg-background";

/**
 * The same width again as a number — **not** as a second source of truth, but
 * because opening animates it (`AppShell`, A13) and motion needs a value for
 * that: `width: "auto"` would not work, because motion would write
 * `width: auto` as an inline style at the end, and that overrides the class
 * `w-[400px]` — the panel would collapse to its content width. Tailwind in turn
 * doesn't accept a computed class, so the number can't come from the constant.
 * The guard instead is a test (`AppShell.test.tsx`): it checks that the class
 * carries exactly this number.
 */
export const ASSISTANT_PANEL_WIDTH_PX = 400;

/**
 * The `id` of the panel shell — on the `aside` on desktop, on the sheet on
 * mobile. The „Fragen" button points to it with `aria-controls` (disclosure
 * pattern), and the focus return in `SiteHeader` uses it to detect whether
 * focus is still inside the panel that is sliding out. Only **one** of the two
 * shells is ever in the tree (desktop or mobile), so the `id` stays unique.
 */
export const ASSISTANT_PANEL_ID = "assistant-panel";

/** The viewport cap from the same class — `max-w-[40vw]`. */
export const ASSISTANT_PANEL_MAX_VW = 0.4;

/**
 * What the browser makes of `w-[400px] max-w-[40vw]`. The animation runs
 * towards exactly this value; if it stubbornly went to 400 px, it would stand
 * still for the last percent on narrow windows, because `max-width` has long
 * since capped it.
 */
export function assistantPanelWidth(viewportWidth: number): number {
  return Math.min(ASSISTANT_PANEL_WIDTH_PX, Math.round(viewportWidth * ASSISTANT_PANEL_MAX_VW));
}

/**
 * The transcript wrapper. `overflow-hidden` belongs to it: without it a long
 * transcript stretches the height chain, and the preview's input row would sit
 * lower than the real panel's — a visible jump in the crossfade. That is
 * exactly why the class lives here and not twice.
 */
export const ASSISTANT_PANEL_TRANSCRIPT_CLASS = "relative min-h-0 flex-1 overflow-hidden";

/** The frame of the input at the bottom — cut like the main chat's. */
export const ASSISTANT_PANEL_FORM_CLASS = "shrink-0 border-t bg-background p-3";

export const ASSISTANT_PANEL_INPUT_BOX_CLASS =
  "relative rounded-2xl border border-[var(--oa-border)] bg-[var(--oa-panel)] p-2 transition-colors focus-within:border-[var(--oa-fg-5)]";

/** The row below the input field holding the send/stop button. */
export const ASSISTANT_PANEL_ACTION_ROW_CLASS = "mt-1 flex min-h-9 items-center gap-1";

